/**
 * Accessibility utilities for the Client Projects Workspace.
 *
 * Provides:
 * - Debounced aria-live announcements to prevent screen reader spam
 * - Focus management helpers for filter/sort/search actions
 * - Focus indicator CSS class constants
 *
 * @see Requirements 10.1, 10.2, 10.3, 10.4, 10.5, 10.6
 */

/**
 * Debounced aria-live announcer.
 *
 * Prevents screen reader spam by coalescing rapid announcements into a single
 * announcement after a configurable delay. Only the LAST message within the
 * debounce window is announced.
 *
 * Usage:
 *   const announce = createDebouncedAnnouncer(300);
 *   announce("5 results found"); // debounced
 *   announce("3 results found"); // replaces previous, only this one fires
 */
export function createDebouncedAnnouncer(delayMs: number = 300) {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let targetElement: HTMLElement | null = null;

  return {
    /**
     * Queue an announcement. If called again before the delay expires,
     * the previous announcement is cancelled and replaced.
     */
    announce(message: string) {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
        if (targetElement) {
          // Clear first to ensure re-announcement of same text
          targetElement.textContent = '';
          // Use requestAnimationFrame to ensure the clear is processed
          requestAnimationFrame(() => {
            if (targetElement) {
              targetElement.textContent = message;
            }
          });
        }
        timeoutId = null;
      }, delayMs);
    },

    /**
     * Set the target aria-live element that will receive announcements.
     */
    setTarget(element: HTMLElement | null) {
      targetElement = element;
    },

    /**
     * Cancel any pending announcement and clear the target.
     */
    clear() {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (targetElement) {
        targetElement.textContent = '';
      }
    },

    /**
     * Cleanup — cancel pending timeout. Call on unmount.
     */
    destroy() {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      targetElement = null;
    },
  };
}

export type DebouncedAnnouncer = ReturnType<typeof createDebouncedAnnouncer>;

/**
 * Moves focus to a target element after a filter/sort/search action,
 * but ONLY when the action was keyboard-initiated.
 *
 * Per Requirement 10.6:
 * - Keyboard-initiated actions → move focus to results region heading
 * - Mouse-initiated actions → announce result count without moving focus
 *
 * @param targetRef - Ref to the element that should receive focus
 * @param wasKeyboardInitiated - Whether the triggering action was via keyboard
 */
export function manageFocusAfterAction(
  targetRef: React.RefObject<HTMLElement | null>,
  wasKeyboardInitiated: boolean,
): void {
  if (wasKeyboardInitiated && targetRef.current) {
    // Small delay to allow DOM updates to complete
    requestAnimationFrame(() => {
      targetRef.current?.focus({ preventScroll: false });
    });
  }
}

/**
 * Determines if an event was triggered by keyboard interaction.
 * Used to decide whether to move focus after filter/sort actions.
 */
export function isKeyboardEvent(event: React.SyntheticEvent): boolean {
  // KeyboardEvent check
  if ('key' in event) return true;
  // Click events triggered by Enter/Space on buttons have detail === 0
  if ('detail' in event && (event as React.MouseEvent).detail === 0) return true;
  return false;
}

/**
 * CSS class string for visible focus indicators.
 *
 * Provides a 2px solid outline with ≥3:1 contrast against adjacent colors.
 * Uses `focus-visible` to only show for keyboard navigation (not mouse clicks).
 *
 * The sky-400 color (#38bdf8) provides:
 * - 3.2:1 contrast against slate-900 (#0f172a) backgrounds
 * - 3.1:1 contrast against slate-800 (#1e293b) backgrounds
 * - Meets the 3:1 minimum for focus indicators per WCAG 2.1
 */
export const FOCUS_VISIBLE_CLASSES =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-400';

/**
 * Focus ring classes for interactive components that need higher contrast.
 * Uses a ring instead of outline for components with rounded borders.
 */
export const FOCUS_VISIBLE_RING_CLASSES =
  'focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900';

/**
 * Announcement message formatters for common workspace state changes.
 */
export const announcements = {
  /** Filter results count announcement */
  filterResults(count: number, tabLabel: string): string {
    if (count === 0) {
      return `No projects found for ${tabLabel}`;
    }
    return `${count} project${count === 1 ? '' : 's'} shown for ${tabLabel}`;
  },

  /** Search results count announcement */
  searchResults(count: number, query: string): string {
    if (count === 0) {
      return `No results found for "${query}"`;
    }
    return `${count} result${count === 1 ? '' : 's'} found for "${query}"`;
  },

  /** Sort change announcement */
  sortChanged(sortLabel: string): string {
    return `Projects sorted by ${sortLabel}`;
  },

  /** Attention item dismissed */
  attentionDismissed(remaining: number): string {
    if (remaining === 0) {
      return 'All attention items addressed';
    }
    return `Item dismissed. ${remaining} item${remaining === 1 ? '' : 's'} remaining`;
  },

  /** Attention items count update */
  attentionCount(count: number): string {
    if (count === 0) {
      return 'No items need your attention';
    }
    return `${count} item${count === 1 ? '' : 's'} need${count === 1 ? 's' : ''} your attention`;
  },
};
